import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  DocumentArrowDownIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

export default function SuperAdminDevTools() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pdfTypes, setPdfTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'super_admin') {
      navigate('/');
      return;
    }
    fetchPdfTypes();
  }, [user, navigate]);

  const fetchPdfTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/dev/pdf-types`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPdfTypes(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des types de PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePdf = async (type) => {
    setGenerating(type);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/dev/generate-pdf/${type}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );

      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier depuis les headers ou utiliser un nom par défaut
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${type}-test.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          fileName = match[1];
        }
      }
      
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Grouper les PDF par catégorie
  const bulletins = Object.entries(pdfTypes).filter(([key]) => key.startsWith('bulletin_'));
  const attestationsAdhesion = Object.entries(pdfTypes).filter(([key]) => key.startsWith('attestation_') && (key.includes('sar') || key.includes('amair')) && !key.includes('service'));
  const attestationsService = Object.entries(pdfTypes).filter(([key]) => key.includes('service'));
  const emargements = Object.entries(pdfTypes).filter(([key]) => key.startsWith('emargement_'));

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-8 py-6">
        <div className="flex items-center gap-3">
          <WrenchScrewdriverIcon className="h-8 w-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Outils de développement
            </h1>
            <p className="text-gray-600 mt-1">
              Générez des PDF de test avec des données fictives pour vérifier le design
            </p>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="px-8 py-6 space-y-6">
        {/* Bulletins d'adhésion */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-blue-600" />
            Bulletins d'adhésion
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bulletins.map(([key, pdf]) => (
              <div
                key={key}
                className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors"
              >
                <h3 className="font-medium text-gray-900">{pdf.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{pdf.description}</p>
                <button
                  onClick={() => handleGeneratePdf(key)}
                  disabled={generating === key}
                  className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
                >
                  {generating === key ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Génération...
                    </>
                  ) : (
                    <>
                      <DocumentArrowDownIcon className="h-4 w-4" />
                      Générer PDF
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Attestations d'adhésion */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-green-600" />
            Attestations d'adhésion
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {attestationsAdhesion.map(([key, pdf]) => (
              <div
                key={key}
                className="border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors"
              >
                <h3 className="font-medium text-gray-900">{pdf.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{pdf.description}</p>
                <button
                  onClick={() => handleGeneratePdf(key)}
                  disabled={generating === key}
                  className="mt-3 flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:bg-gray-400 transition-colors"
                >
                  {generating === key ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Génération...
                    </>
                  ) : (
                    <>
                      <DocumentArrowDownIcon className="h-4 w-4" />
                      Générer PDF
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Attestations de service */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-honey-600" />
            Attestations de service
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {attestationsService.map(([key, pdf]) => (
              <div
                key={key}
                className="border border-gray-200 rounded-lg p-4 hover:border-honey-300 transition-colors"
              >
                <h3 className="font-medium text-gray-900">{pdf.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{pdf.description}</p>
                <button
                  onClick={() => handleGeneratePdf(key)}
                  disabled={generating === key}
                  className="mt-3 flex items-center gap-2 px-4 py-2 bg-honey-600 text-white text-sm rounded-lg hover:bg-honey-700 disabled:bg-gray-400 transition-colors"
                >
                  {generating === key ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Génération...
                    </>
                  ) : (
                    <>
                      <DocumentArrowDownIcon className="h-4 w-4" />
                      Générer PDF
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Feuilles d'émargement */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <DocumentTextIcon className="h-5 w-5 text-purple-600" />
            Feuilles d'émargement
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {emargements.map(([key, pdf]) => (
              <div
                key={key}
                className="border border-gray-200 rounded-lg p-4 hover:border-purple-300 transition-colors"
              >
                <h3 className="font-medium text-gray-900">{pdf.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{pdf.description}</p>
                <button
                  onClick={() => handleGeneratePdf(key)}
                  disabled={generating === key}
                  className="mt-3 flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:bg-gray-400 transition-colors"
                >
                  {generating === key ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Génération...
                    </>
                  ) : (
                    <>
                      <DocumentArrowDownIcon className="h-4 w-4" />
                      Générer PDF
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Outils de migration utilisateurs */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UserGroupIcon className="h-5 w-5 text-honey-600" />
            Migration des utilisateurs
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Créez des comptes utilisateurs à partir des adhésions existantes et gérez les activations.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/super-admin/pending-activations"
              className="border border-gray-200 rounded-lg p-4 hover:border-honey-300 transition-colors flex items-center justify-between group"
            >
              <div>
                <h3 className="font-medium text-gray-900">Comptes en attente d'activation</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Gérer les comptes créés à partir des adhésions
                </p>
              </div>
              <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-honey-600 transition-colors" />
            </Link>
          </div>
        </div>

        {/* Note d'information */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-800">
            <strong>Note :</strong> Ces PDF sont générés avec des données fictives à des fins de développement uniquement.
            Modifiez les fonctions dans <code className="bg-yellow-100 px-1 rounded">pdfService.js</code> ou <code className="bg-yellow-100 px-1 rounded">emargementService.js</code> puis régénérez pour voir vos modifications.
          </p>
        </div>
      </div>
    </div>
  );
}
